import { domClassMerge } from '@propertyfinder/helpers';
import { domQuerySelector } from '@propertyfinder/pf-frontend-common/src/helper/dom/query-selector';
import { FunctionalComponent, h } from 'preact';
import { memo, useEffect } from 'preact/compat';
import { useInView } from 'react-intersection-observer';

import { chooseAOrB } from 'common/helper/choose/a-or-b';
import { chooseAOrNull } from 'common/helper/choose/a-or-null';
import { galleryGetGalleryScrollItems } from 'common/helper/gallery/get-gallery-scroll-items';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { stringToggle } from 'common/helper/string/toggle';
import { GalleryScrollComponent } from 'common/module/gallery-scroll/component';
import { LazyLoadingComponent } from 'common/module/lazy-loading/component';
import { preactLifecycleComponent } from 'common/module/preact/lifecycle-component';
import { ProgressiveImageComponent } from 'common/module/progressive-image/component';
import { propertyCardSeoText } from 'common/module/property/card/seo-text';
import { PropertyCardTemplatePropsInterface } from 'common/module/property/card/template-props.interface';
import { TagListComponent } from 'common/module/tag-list/component';
import { PropertyCardSectionsIntroTemplate } from 'desktop/module/property/card/sections/intro/template';

import { PropertyCardIconGroupTemplate } from './icon-group/template';
import { PropertyCardSectionsFooterComponent } from './sections/footer/component';
import { PropertyCardSectionsSpecificationsTemplate } from './sections/specifications/template';

const Markup: FunctionalComponent<PropertyCardTemplatePropsInterface> = (props): preact.JSX.Element => {
  /**
   * Check if property has several images
   */
  const hasSeveralImages = props.property.property_images?.length > 1;

  const { ref: containerRef, inView } = useInView({
    root: null,
    rootMargin: '0px 0px 0px 0px',
    threshold: 0.5,
  });

  useEffect(() => {
    if (inView) {
      props.onEnteringScreen?.(props.property.id);
    }
  }, [inView]);

  return chooseAOrNull(
    <article
      ref={containerRef}
      className={domClassMerge('card', {
        'card--disabled': props.isDisabled,
        'card--community-expert': props.property.areaSpecialistProperty,
      })}
    >
      <a
        href={chooseAOrB('#', props.property.links.self)(props.isDisabled)}
        className='card__link'
        onClick={chooseAOrB(
          (e: Event) => e.preventDefault(),
          (e: Event) => props.onClickProperty(e, props.property)
        )(props.isDisabled)}
        title={propertyCardSeoText(props.property)}
      />
      <div className='card__body'>
        <div
          className={`card__image card__image--property ${hasSeveralImages && 'card__image--gallery'}`}
          onClick={(e: Event): void => hasSeveralImages && e.preventDefault()}
        >
          <LazyLoadingComponent
            visible={!props.isImageNotVisible}
            className={'card__image-placeholder'}
            options={{ rootMargin: '700px 0px 700px 0px' }}
          >
            {chooseAOrB(
              <GalleryScrollComponent
                key={props.property.id}
                items={galleryGetGalleryScrollItems(props.property)}
                onScroll={props.onGalleryScroll}
                alt={propertyCardSeoText(props.property)}
                title={propertyCardSeoText(props.property)}
                placeholder={__('assets/common/property-placeholder-sm.svg')}
                whatsappShown={!!domQuerySelector(document, '[data-qs="search-property"]')}
              />,
              <div
                onClick={(): void =>
                  props.onClickImage({
                    propertyModel: props.property,
                    sourceUrl: props.property.links.image_property_small,
                    imageIndex: 1,
                    isCoverImage: 1,
                  })
                }
              >
                <ProgressiveImageComponent
                  src={props.property.links.image_property_small}
                  classname={`card__img card__img--style1 ${stringToggle('card__img--disabled')(props.isDisabled)}`}
                  loadedClass='card__img--loaded'
                  alt={propertyCardSeoText(props.property)}
                  title={propertyCardSeoText(props.property)}
                  placeholder={__('assets/common/property-placeholder-sm.svg')}
                  preloader={__('assets/common/image-loader.svg')}
                />
              </div>
            )(hasSeveralImages)}
          </LazyLoadingComponent>
          <PropertyCardIconGroupTemplate
            property={props.property}
            class='card__image-icons-area'
            iconOptions={{
              images: {
                isEnabled: false,
              },
              video: {
                isEnabled: true,
              },
              360: {
                isEnabled: !props.property.meta?.video_metadata?.url,
              },
            }}
            isDisabled={props.isDisabled}
          />
          <TagListComponent
            property={props.property}
            className={domClassMerge('card__image-tag--container', { 'card__image-tag--disabled': props.isDisabled })}
          />
        </div>
        <div className='card__content'>
          <PropertyCardSectionsIntroTemplate brokerImageVisible={!props.isImageNotVisible} property={props.property} />
          <div className='card__top'>
            <div className='card__left'>
              <div className='card__actions-area'>
                {chooseAOrNull(
                  <div className='card__expired'>{i18nTranslate('This property is no longer available')}</div>
                )(props.property.is_expired)}
              </div>
            </div>
          </div>
          <PropertyCardSectionsSpecificationsTemplate
            property={props.property}
            disabled={props.isDisabled}
            showIcons={true}
          />
        </div>
      </div>
      <PropertyCardSectionsFooterComponent {...props} />
    </article>
  )(!!props.property);
};

const MarkupMemoized = memo(Markup);

export const PropertyCardTemplate = preactLifecycleComponent(MarkupMemoized);
